import { FriendshipWithUser } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { Button, EmptyState, LoadingSpinner, PatternBackground, UserCard } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { FlatList, RefreshControl, Text, View } from 'react-native';
import { createStyles } from './FriendsScreen.styles';

export interface FriendsScreenProps {
  friends: FriendshipWithUser[];
  loading: boolean;
  loadingMore: boolean;
  currentUserId: string;
  onRemoveFriend: (friendshipId: string, friendName?: string) => void;
  onUserPress: (userId: string) => void;
  onRefresh: () => void;
  onLoadMore: () => void;
  colors: {
    background: string;
    surface: string;
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    primary: string;
    primaryText: string;
    error: string;
  };
}

export const FriendsScreen = ({
  friends,
  loading,
  loadingMore,
  currentUserId,
  onRemoveFriend,
  onUserPress,
  onRefresh,
  onLoadMore,
  colors,
}: FriendsScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  if (loading) {
    return (
      <PatternBackground>
        <LoadingSpinner />
      </PatternBackground>
    );
  }

  const renderFriendItem = ({ item }: { item: FriendshipWithUser }) => {
    // Determine which user is the friend (the one who is NOT the current user)
    const friendId = item.requesterId === currentUserId ? item.addresseeId : item.requesterId;

    return (
      <UserCard
        userId={friendId}
        userName={item.userName}
        userEmail={item.userEmail}
        userAvatarUrl={item.userAvatarUrl}
        onPress={() => onUserPress(friendId)}
        actions={
          <Button
            title={t('common.remove')}
            onPress={() => onRemoveFriend(item.id, item.userName)}
            variant="secondary"
            size="small"
            colors={colors}
          />
        }
        colors={colors}
      />
    );
  };

  return (
    <PatternBackground>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <Text style={[styles.count, { color: colors.textSecondary }]}>
            {friends.length} {friends.length === 1 ? t('social.friend') : t('social.friends').toLowerCase()}
          </Text>
          <Ionicons name="people" size={24} color={colors.primary} />
        </View>
      </View>

      <FlatList
        data={friends}
        renderItem={renderFriendItem}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.list}
        onEndReached={onLoadMore}
        onEndReachedThreshold={0.5}
        refreshControl={
          <RefreshControl refreshing={loading} onRefresh={onRefresh} tintColor={colors.primary} />
        }
        ListFooterComponent={loadingMore ? <LoadingSpinner /> : null}
        ListEmptyComponent={
          <EmptyState
            icon={<Ionicons name="people-outline" size={52} color={colors.textTertiary} />}
            title={t('social.noFriendsYet')}
            subtitle={t('social.whenAddFriendsAppear')}
            colors={colors}
          />
        }
      />
    </PatternBackground>
  );
};
